import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { format, parseISO } from 'date-fns';
import {
  DEFAULT_MARKS,
  marksToScoreLevelMap,
  marksToYAxisLevels,
} from '../StudentsProgress.mock';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const ProgressLineChart = ({ data, marks, size = 'large' }) => {
  const theme = useTheme();

  // Use marks from API or fallback to defaults
  const effectiveMarks = marks && marks.length > 0 ? marks : DEFAULT_MARKS;

  // Generate score to level map from marks
  const scoreLevelMap = useMemo(
    () => marksToScoreLevelMap(effectiveMarks),
    [effectiveMarks]
  );

  // Generate Y-axis levels from marks
  const yAxisLevels = useMemo(
    () => marksToYAxisLevels(effectiveMarks, 5),
    [effectiveMarks]
  );

  // Map yAxisLevels to sequential indices (0, 1, 2, 3, 4) for uniform spacing
  const yAxisIndices = useMemo(() => {
    return yAxisLevels.map((level, idx) => ({
      index: idx,
      score: level.score,
      label: level.label,
    }));
  }, [yAxisLevels]);

  // Create a function to map score to uniform index based on yAxisLevels
  const scoreToIndex = useMemo(() => {
    return (score) => {
      // Find the two yAxisLevels that the score falls between
      for (let i = 0; i < yAxisIndices.length - 1; i++) {
        const lower = yAxisIndices[i];
        const upper = yAxisIndices[i + 1];
        if (score >= lower.score && score <= upper.score) {
          // Interpolate between the two sequential indices
          const ratio =
            (score - lower.score) / (upper.score - lower.score || 1);
          return lower.index + ratio;
        }
      }
      // If score is at or above the max, return the last index
      if (
        yAxisIndices.length > 0 &&
        score >= yAxisIndices[yAxisIndices.length - 1].score
      ) {
        return yAxisIndices[yAxisIndices.length - 1].index;
      }
      // If score is below the min, return the first index
      return yAxisIndices.length > 0 ? yAxisIndices[0].index : 0;
    };
  }, [yAxisIndices]);

  if (!data || data.length === 0) {
    return null;
  }

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateFull = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  };

  const chartData = {
    labels: data.map((item) => formatDate(item.date)),
    datasets: [
      {
        data: data.map((item) => scoreToIndex(item.score_value)),
        borderColor: theme.config.colors.primary,
        backgroundColor: theme.config.colors.primary,
        borderWidth: 2,
        pointRadius: 5,
        pointBackgroundColor: theme.config.colors.primary,
        pointBorderColor: theme.config.colors.light,
        pointBorderWidth: 2,
        tension: 0.4,
        fill: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const item = data[index];
            return formatDate(item.date);
          },
          label: () => null,
          footer: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const currentItem = data[index];
            const currentDate = currentItem.date;

            // Find all items with the same date
            const sameDayItems = data.filter(
              (item) => item.date === currentDate
            );

            // Return array of strings for multiple lines
            return sameDayItems.map((item) => {
              const level = scoreLevelMap[item.score_value] || item.level;
              return `Level: ${level} - ${formatDateFull(item.date)}`;
            });
          },
        },
        backgroundColor: theme.config.colors.secondary,
        titleColor: theme.config.colors.light,
        bodyColor: theme.config.colors.light,
        footerColor: theme.config.colors.light,
        footerFont: { weight: 'normal' },
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: {
          color: theme.config.colors.gray_100,
          drawBorder: false,
        },
        ticks: {
          color: theme.config.colors.gray_400,
          font: {
            size: 11,
          },
          maxRotation: 0,
          autoSkip: false,
          maxTicksLimit: data.length,
          callback: function (value, index) {
            const totalPoints = data.length;
            // Only show label for first and last points
            if (index === 0 || index === totalPoints - 1) {
              return this.getLabelForValue(value);
            }
            return '';
          },
        },
      },
      y: {
        min: 0,
        max: yAxisIndices.length > 0 ? yAxisIndices.length - 1 : 4,
        grid: {
          color: theme.config.colors.gray_100,
          drawBorder: false,
        },
        ticks: {
          color: theme.config.colors.gray_400,
          font: {
            size: 11,
          },
          stepSize: 1,
          autoSkip: false,
          callback: (value) => {
            const level = yAxisIndices.find((m) => m.index === value);
            return level ? level.label : '';
          },
        },
      },
    },
  };

  return (
    <div
      style={{ height: size === 'small' ? '18rem' : '24rem', width: '100%' }}
    >
      <Line data={chartData} options={options} />
    </div>
  );
};

ProgressLineChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      level: PropTypes.string.isRequired,
      score_value: PropTypes.number.isRequired,
    })
  ),
  marks: PropTypes.arrayOf(
    PropTypes.shape({
      score: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  size: PropTypes.oneOf(['small', 'large']),
};
